import { createPortal } from 'react-dom';
import { useRef, useLayoutEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { formatPrice } from '@/hooks/usePrice.js';

const OFFSET_X = 14;
const OFFSET_Y = 14;
const MARGIN   = 8;

/**
 * Portal-based tooltip rendered as a fixed HTML div outside the SVG.
 */
export default function Tooltip( { visible, mouseX, mouseY, state, tile, fieldConfig, occupiedData } ) {
	const ref     = useRef( null );
	const content = renderContent( state, tile, fieldConfig, occupiedData );

	useLayoutEffect( () => {
		const el = ref.current;
		if ( ! el ) return;
		if ( ! visible || ! content ) { el.style.visibility = 'hidden'; return; }

		const w  = el.offsetWidth;
		const h  = el.offsetHeight;
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		let left = mouseX + OFFSET_X;
		let top  = mouseY - OFFSET_Y - h;

		if ( left + w > vw - MARGIN ) left = mouseX - OFFSET_X - w;
		if ( left < MARGIN )          left = MARGIN;
		if ( top  < MARGIN )          top  = mouseY + OFFSET_Y;
		if ( top + h > vh - MARGIN )  top  = vh - h - MARGIN;

		el.style.left       = left + 'px';
		el.style.top        = top  + 'px';
		el.style.visibility = 'visible';
	} );

	if ( ! visible || ! content ) return null;

	return createPortal(
		<div
			ref={ref}
			className="wsp-tooltip"
			style={{ position: 'fixed', left: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', zIndex: 99999 }}
		>
			{content}
		</div>,
		document.body
	);
}

// ─────────────────────────────────────────────────────────────────────────────

function renderContent( state, tile, fieldConfig, occupiedData ) {
	// ── Occupied / pending ────────────────────────────────────────────────────
	if ( state === 'pending' || state === 'occupied' ) {
		const badgeLabel = state === 'occupied'
			? __( 'Sold', 'web-sponsors-pitch' )
			: __( 'Reserved', 'web-sponsors-pitch' );

		// Tile label (bundle name or category)
		let tileLabel = null;
		if ( fieldConfig ) {
			if ( tile?.bundleId ) {
				tileLabel = fieldConfig.bundles?.[ tile.bundleId ]?.label ?? null;
			} else if ( tile ) {
				const cat = ( fieldConfig.categories ?? {} )[ tile.category ?? 'default' ]
					?? ( fieldConfig.categories ?? {} )[ 'default' ];
				tileLabel = cat?.label ?? null;
			}
		}

		const hasName    = state === 'occupied' && occupiedData && ! occupiedData.anonymous && occupiedData.sponsor_name;
		const hasContent = state === 'occupied' && occupiedData && ! occupiedData.anonymous && occupiedData.content;
		const isAnon     = state === 'occupied' && occupiedData?.anonymous;

		return (
			<>
				<div className="wsp-tooltip__header">
					<p className={`wsp-tooltip__badge wsp-tooltip__badge--${ state }`}>{badgeLabel}</p>
					{tileLabel && <p className="wsp-tooltip__label">{tileLabel}</p>}
				</div>
				<hr className="wsp-tooltip__divider" />
				<div className="wsp-tooltip__sponsor-layout">
					{! isAnon && occupiedData?.thumbnail && (
						<img
							src={occupiedData.thumbnail}
							alt={occupiedData.sponsor_name || __( 'Sponsor image', 'web-sponsors-pitch' )}
							className="wsp-tooltip__image"
						/>
					)}
					<div className="wsp-tooltip__sponsor-content">
						{isAnon    && <p className="wsp-tooltip__anon">{__( 'Anonymous sponsor', 'web-sponsors-pitch' )}</p>}
						{hasName   && <p className="wsp-tooltip__name">{occupiedData.sponsor_name}</p>}
						{hasContent && <p className="wsp-tooltip__content">{occupiedData.content}</p>}
					</div>
				</div>
			</>
		);
	}

	// ── Free / selected ───────────────────────────────────────────────────────
	if ( state !== 'free' && state !== 'selected' ) return null;
	if ( ! fieldConfig ) return null;

	const basePrice  = fieldConfig.base_price ?? 100;
	const categories = fieldConfig.categories ?? {};

	let displayLabel, displayPrice, catLabel;

	if ( tile?.bundleId ) {
		const bundle  = fieldConfig.bundles?.[ tile.bundleId ];
		displayLabel  = bundle?.label ?? tile.bundleId;
		const catSlug = tile.category ?? 'default';
		const factor  = categories[ catSlug ]?.factor ?? 1.0;
		catLabel      = null; // bundles already have a descriptive label — category redundant
		displayPrice  = ( tile.count ?? 1 ) * basePrice * factor;
	} else {
		const catSlug = tile?.category ?? 'default';
		const cat     = categories[ catSlug ] ?? categories[ 'default' ];
		displayLabel  = cat?.label ?? catSlug;
		catLabel      = null; // label IS the category for standalone tiles
		displayPrice  = basePrice * ( cat?.factor ?? 1.0 );
	}

	const sizeLine = tile
		? `${ Number( tile.totalArea ?? ( tile.w * tile.h ) ).toFixed( 1 ) } m²`
		: null;

	return (
		<>
			{displayLabel && <p className="wsp-tooltip__label">{displayLabel}</p>}
			{catLabel     && <p className="wsp-tooltip__category">{catLabel}</p>}
			{sizeLine     && <p className="wsp-tooltip__size">{sizeLine}</p>}
			<hr className="wsp-tooltip__divider" />
			<p className="wsp-tooltip__price">{formatPrice( displayPrice ?? 0 )}</p>
		</>
	);
}
